// frontend/src/components/UploadStatement.js
import React, { useState } from 'react';
import axios from '../api';
import EditStatement from './EditStatement';
import Loader from './Loader';

function UploadStatement() {
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const token = localStorage.getItem('token');

  const ALLOWED_TYPES = [
    'application/pdf',
    'image/png',
    'image/jpeg',
    'image/jpg',
    'text/csv',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ];
  const ALLOWED_EXTENSIONS = ['.pdf', '.png', '.jpg', '.jpeg', '.csv', '.docx'];
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

  const validateFile = (selectedFile) => {
    setError('');
    
    if (!selectedFile) {
      setError('Please select a file');
      return false;
    }
    
    // Check file size
    if (selectedFile.size > MAX_FILE_SIZE) {
      setError('File size exceeds 10MB limit');
      return false;
    }
    
    // Check file type
    const fileExtension = '.' + selectedFile.name.split('.').pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(fileExtension)) {
      setError('Invalid file type. Allowed: PDF, PNG, JPG, CSV, DOCX');
      return false;
    }
    
    // Additional MIME type check
    if (selectedFile.type && !ALLOWED_TYPES.includes(selectedFile.type)) {
      setError('Invalid file format detected');
      return false;
    }
    
    return true;
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    
    if (validateFile(selectedFile)) {
      setFile(selectedFile);
      setError('');
    } else {
      setFile(null);
      e.target.value = ''; // Reset file input
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError("Please select a file first!");
      return;
    }

    if (!validateFile(file)) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await axios.post('/parse', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      });

      setTimeout(() => {
        setParsedData(res.data);
        setLoading(false);
      }, 1500);
    } catch (err) {
      setLoading(false);
      console.error("Upload error:", err);
      const errMsg = err.response?.data?.error || err.message;
      setError(`File upload failed: ${errMsg}`);
    }
  };

  if (loading) return <Loader />;

  return (
    <div>
      <div className="card">
        <h2 style={{ 
          fontSize: '1.8rem', 
          marginBottom: '1.5rem',
          background: 'linear-gradient(135deg, #00ffff, #ff00ff)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }}>
          Upload Your Statement
        </h2>
        
        <div className="file-upload-container">
          <label className="file-upload-label" htmlFor="file-input">
            <div className="upload-icon">📄</div>
            <div className="upload-text">
              {file ? file.name : 'Click to upload or drag and drop'}
            </div>
            <div className="upload-subtext">
              PDF, PNG, JPG, CSV, DOCX (Max 10MB)
            </div>
          </label>
          <input
            id="file-input"
            type="file"
            accept=".pdf,.png,.jpg,.jpeg,.csv,.docx"
            onChange={handleFileChange}
          />
        </div>

        {file && !error && (
          <div className="file-selected">
            <span>✓</span>
            <span>{file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
          </div>
        )}

        {error && (
          <div style={{
            marginTop: '1rem',
            padding: '1rem',
            background: 'rgba(255, 68, 68, 0.1)',
            border: '1px solid rgba(255, 68, 68, 0.3)',
            borderRadius: '8px',
            color: '#ff4444',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <span>⚠️</span>
            <span>{error}</span>
          </div>
        )}

        <button 
          onClick={handleUpload} 
          className="neon-button"
          style={{ marginTop: '1.5rem', width: '100%' }}
          disabled={!file || !!error}
        >
          <span className="text">Parse Statement</span>
        </button>
      </div>

      {parsedData && <EditStatement data={parsedData} />}
    </div>
  );
}

export default UploadStatement;